import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import {
  underwritingAgentsApi,
  underwritingTemplateRulesQueryKey
} from "api/underwriting-agents/underwriting-agents";
import type {
  TemplateRulesDraft,
  UnderwritingReportKey,
  UnderwritingRule
} from "api/underwriting-agents/types";
import type { Template } from "types/landlord/verificationTemplate";
import {
  type RuleDraft,
  toRuleDraft
} from "shared/app/UnderwritingAgents/rules";
import { getErrorMessage } from "utils/error";

type Adjustments = { excludedIds: number[]; ownRules: RuleDraft[] };

const NO_ADJUSTMENTS: Adjustments = { excludedIds: [], ownRules: [] };

const toAgentRuleDraft = (rule: UnderwritingRule): RuleDraft => ({
  ...toRuleDraft(rule),
  fromAgent: true
});

const toAdjustments = (
  agentRules: UnderwritingRule[],
  templateRules: UnderwritingRule[]
): Adjustments => ({
  excludedIds: agentRules
    .filter((rule) => !templateRules.some(({ id }) => id === rule.id))
    .map(({ id }) => id),
  ownRules: templateRules
    .filter((rule) => rule.verification_template_identifier !== null)
    .map(toRuleDraft)
});

const isAdjusted = ({ excludedIds, ownRules }: Adjustments) =>
  excludedIds.length > 0 || ownRules.length > 0;

const sameAdjustments = (a: Adjustments, b: Adjustments) =>
  a.excludedIds.length === b.excludedIds.length &&
  a.excludedIds.every((id) => b.excludedIds.includes(id)) &&
  a.ownRules.length === b.ownRules.length &&
  a.ownRules.every(
    (rule, index) =>
      rule.savedId === b.ownRules[index].savedId &&
      rule.report_key === b.ownRules[index].report_key &&
      rule.text === b.ownRules[index].text
  );

const toPayload = ({ excludedIds, ownRules }: Adjustments) => ({
  excluded_agent_rule_ids: excludedIds,
  rules: ownRules.map(({ savedId, report_key, text }) => ({
    id: savedId,
    report_key,
    text
  }))
});

/**
 * Saves the rule changes a template kept while it didn't have the agent yet,
 * once the template is saved with it.
 */
export const useSaveTemplateRulesDraft = () => {
  const queryClient = useQueryClient();

  return async (draft: TemplateRulesDraft | undefined, saved: Template) => {
    if (!draft || saved.underwriting_agent?.id !== draft.agentId) return;
    try {
      await underwritingAgentsApi.saveTemplateRules({
        agentId: draft.agentId,
        verification_template_identifier: saved.id,
        ...toPayload(draft)
      });
      await queryClient.invalidateQueries({
        queryKey: underwritingTemplateRulesQueryKey(draft.agentId, saved.id)
      });
    } catch (err) {
      toast.error(
        `The template was saved, but its underwriting rules weren't: ${getErrorMessage(err)}`
      );
    }
  };
};

/**
 * A screening template's underwriting rules (BSCPF-6141): its agent's rules,
 * the ones it leaves out switched off, then its own. Save rules saves them at
 * once when the saved template has the agent. Before that the API doesn't take
 * them, so `keep` holds them on the template and they are saved with it.
 */
export const useTemplateRulesForm = ({
  agentId,
  templateId,
  enabled,
  keep
}: {
  agentId: number;
  templateId: string;
  enabled: boolean;
  keep?: {
    draft?: TemplateRulesDraft;
    onKeep: (draft: TemplateRulesDraft | undefined) => void;
  };
}) => {
  const queryClient = useQueryClient();
  const isKept = keep !== undefined;
  const keptDraft = keep?.draft;
  const queryKey = underwritingTemplateRulesQueryKey(agentId, templateId);

  const [report, setReport] = useState<UnderwritingReportKey>("income_history");
  const [draft, setDraft] = useState<Adjustments | null>(null);

  const rulesQuery = useQuery({
    queryKey: isKept ? [...queryKey, "UNSAVED"] : queryKey,
    queryFn: async () => {
      // Not saved with the agent yet: it starts from the agent's rules.
      if (isKept) {
        const agentRules = await underwritingAgentsApi.listRules(agentId);
        return { agentRules, templateRules: agentRules };
      }
      const [agentRules, templateRules] = await Promise.all([
        underwritingAgentsApi.listRules(agentId),
        underwritingAgentsApi.listTemplateRules(agentId, templateId)
      ]);
      return { agentRules, templateRules };
    },
    enabled,
    refetchOnWindowFocus: true
  });

  const agentRules = useMemo(
    () => rulesQuery.data?.agentRules ?? [],
    [rulesQuery.data]
  );
  const saved = useMemo<Adjustments>(() => {
    if (keptDraft) return keptDraft;
    return rulesQuery.data
      ? toAdjustments(rulesQuery.data.agentRules, rulesQuery.data.templateRules)
      : NO_ADJUSTMENTS;
  }, [keptDraft, rulesQuery.data]);
  // What the agent reviews with until the next change: the card counts these.
  const keptRules = useMemo(
    () => [
      ...agentRules.filter(({ id }) => !saved.excludedIds.includes(id)),
      ...saved.ownRules
    ],
    [agentRules, saved]
  );
  const current = draft ?? saved;

  // The agent's rules can't be edited here, only switched off for this
  // template: an off one stays in its place, greyed.
  const rules = useMemo<RuleDraft[]>(
    () => [
      ...agentRules.map((rule) => ({
        ...toAgentRuleDraft(rule),
        off: current.excludedIds.includes(rule.id)
      })),
      ...current.ownRules
    ],
    [agentRules, current]
  );
  const adjustedReports = useMemo(
    () => [
      ...new Set(
        rules
          .filter((rule) => (rule.fromAgent ? rule.off : true))
          .map((rule) => rule.report_key)
      )
    ],
    [rules]
  );

  const update = (change: (prev: Adjustments) => Adjustments) =>
    setDraft((prev) => change(prev ?? saved));

  const addRule = (report_key: UnderwritingReportKey, text: string) =>
    update((prev) => ({
      ...prev,
      ownRules: [...prev.ownRules, { key: uuidv4(), report_key, text }]
    }));

  const editRule = ({ key }: RuleDraft, text: string) =>
    update((prev) => ({
      ...prev,
      ownRules: prev.ownRules.map((rule) =>
        rule.key === key ? { ...rule, text } : rule
      )
    }));

  const removeRule = ({ key, savedId, fromAgent }: RuleDraft) =>
    update((prev) =>
      fromAgent && savedId !== undefined
        ? { ...prev, excludedIds: [...prev.excludedIds, savedId] }
        : {
            ...prev,
            ownRules: prev.ownRules.filter((rule) => rule.key !== key)
          }
    );

  const restoreRule = ({ savedId }: RuleDraft) =>
    update((prev) => ({
      ...prev,
      excludedIds: prev.excludedIds.filter((id) => id !== savedId)
    }));

  const toggleRule = (rule: RuleDraft, on: boolean) =>
    on ? restoreRule(rule) : removeRule(rule);

  const reportOf = (ruleId: number) =>
    agentRules.find((rule) => rule.id === ruleId)?.report_key;

  const resetReport = (report_key: UnderwritingReportKey) =>
    update((prev) => ({
      excludedIds: prev.excludedIds.filter((id) => reportOf(id) !== report_key),
      ownRules: prev.ownRules.filter((rule) => rule.report_key !== report_key)
    }));

  const resetAll = () => setDraft(NO_ADJUSTMENTS);

  const open = () => setDraft(saved);
  const close = () => setDraft(null);

  const saveMutation = useMutation({
    mutationKey: [...queryKey, "UPDATE"],
    mutationFn: (adjustments: Adjustments) =>
      underwritingAgentsApi.saveTemplateRules({
        agentId,
        verification_template_identifier: templateId,
        ...toPayload(adjustments)
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      setDraft(null);
      toast.success("Rules saved");
    },
    onError: (err) => toast.error(getErrorMessage(err))
  });

  const save = () => {
    if (!keep) return saveMutation.mutate(current);
    // Kept on the template until it is saved: nothing changed is nothing kept.
    keep.onKeep(isAdjusted(current) ? { agentId, ...current } : undefined);
    setDraft(null);
  };

  return {
    rulesQuery,
    rules,
    keptRules,
    adjustedReports,
    report,
    setReport,
    addRule,
    editRule,
    removeRule,
    toggleRule,
    resetReport,
    resetAll,
    isOpen: draft !== null,
    open,
    close,
    isAdjusted: isAdjusted(saved),
    isDraftAdjusted: isAdjusted(current),
    hasChanges: draft !== null && !sameAdjustments(draft, saved),
    save,
    isSaving: saveMutation.isPending
  };
};
